import {
  useArtifactCanvas,
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasInlineError,
  ArtifactCanvasSaveActions,
  useMarkdownArtifactDraft,
  RichTextCharCount,
  RichTextEditor,
  Input,
  Label,
  MARKDOWN_FIELD_MAX_LENGTH,
  markdownFieldLengthError,
} from '@yeshuman/sdk'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  type OrganisationOpportunity,
  useOpportunity,
  useOrganisationOpportunities,
} from '../../hooks/use-organisation-opportunities'
import { BiasCoachBanner } from '../bias/bias-coach-banner'
import { detect, joinedText } from '../../lib/bias-guardrail'

interface JobCreateDocumentCanvasProps {
  organisationSlug: string
  onClose: () => void
  onCreated: (created: OrganisationOpportunity) => void
}

function requiredTitleDescriptionMessage(title: string, description: string): string | null {
  const hasTitle = Boolean(title.trim())
  const hasDescription = Boolean(description.trim())
  if (hasTitle && hasDescription) return null
  if (!hasTitle && !hasDescription) return 'Job title and description are required'
  if (!hasTitle) return 'Job title is required'
  return 'Job description is required'
}

export function JobCreateDocumentCanvas({
  organisationSlug,
  onClose,
  onCreated,
}: JobCreateDocumentCanvasProps) {
  const { setCanvasSurface } = useArtifactCanvas()
  const [title, setTitle] = useState('')
  const [draftId, setDraftId] = useState<string | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [mutationError, setMutationError] = useState<string | null>(null)
  const titleInputRef = useRef<HTMLInputElement>(null)
  const lastServerTitleRef = useRef('')
  const openingDraftRef = useRef(false)
  const live = useOpportunity(draftId ?? undefined)
  const descriptionDraft = useMarkdownArtifactDraft(
    live.data?.description ?? '',
    draftId ?? organisationSlug,
  )
  const { createOpportunity, isCreating, updateOpportunity, isUpdating } =
    useOrganisationOpportunities(organisationSlug)
  const errorMessage = validationError || mutationError
  const isSaving = isCreating || isUpdating
  const biasFindings = useMemo(
    () => detect(joinedText(title, descriptionDraft.value), 'job_description'),
    [title, descriptionDraft.value],
  )

  // Focus title without scrolling the jobs list under the pane (autoFocus scrollIntoView).
  useEffect(() => {
    titleInputRef.current?.focus({ preventScroll: true })
  }, [])

  useEffect(() => {
    if (!organisationSlug || draftId || openingDraftRef.current) return
    openingDraftRef.current = true
    void createOpportunity({
      title: '',
      description: '',
      skills: [],
    })
      .then((created) => {
        setDraftId(created.id)
        lastServerTitleRef.current = created.title ?? ''
        setCanvasSurface({
          intent: 'create',
          entity: 'opportunity',
          id: created.id,
          mode: 'canvas',
          fields: ['title', 'description'],
        })
      })
      .catch((error: unknown) => {
        openingDraftRef.current = false
        setMutationError(error instanceof Error ? error.message : 'Could not start job draft')
      })
  }, [createOpportunity, draftId, organisationSlug, setCanvasSurface])

  useEffect(() => {
    const nextTitle = live.data?.title
    if (nextTitle == null) return
    if (nextTitle !== lastServerTitleRef.current) {
      lastServerTitleRef.current = nextTitle
      setTitle(nextTitle)
    }
  }, [live.data?.title])

  const persistJob = () => {
    // Prefer live Plate count — Preview serialize is debounced, and over-limit
    // pastes used to never reach draft.value (see clampRichTextChange).
    if (descriptionDraft.charCount > MARKDOWN_FIELD_MAX_LENGTH) {
      setValidationError(
        `Job description must be at most ${MARKDOWN_FIELD_MAX_LENGTH} characters.`,
      )
      return
    }

    const description = descriptionDraft.value
    const titleDescriptionError = requiredTitleDescriptionMessage(title, description)
    if (titleDescriptionError) {
      setValidationError(titleDescriptionError)
      return
    }

    const descriptionLengthError = markdownFieldLengthError(description.trim(), 'Job description')
    if (descriptionLengthError) {
      setValidationError(descriptionLengthError)
      return
    }

    setValidationError(null)
    setMutationError(null)

    const payload = {
      title: title.trim(),
      description: description.trim(),
      skills: [],
    }

    const save = draftId
      ? updateOpportunity({ id: draftId, data: payload })
      : createOpportunity(payload)

    void save
      .then((created) => {
        onCreated(created)
        onClose()
      })
      .catch((error: unknown) => {
        setMutationError(error instanceof Error ? error.message : 'Could not create job')
      })
  }

  return (
    <ArtifactCanvasBody className="flex min-h-0 flex-1 flex-col space-y-0 p-0">
      <form className="flex min-h-0 flex-1 flex-col" onSubmit={(e) => e.preventDefault()}>
        <div className="px-6 pt-6 pb-2">
          <Label htmlFor="artifact-create-job-title" className="sr-only">
            Job title (required)
          </Label>
          <Input
            ref={titleInputRef}
            id="artifact-create-job-title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value)
              setValidationError(null)
              setMutationError(null)
            }}
            placeholder="Job title"
            required
            aria-required="true"
            aria-invalid={!title.trim() && validationError != null}
            className="h-auto rounded-none border-0 bg-transparent px-0 py-1 text-2xl font-semibold tracking-tight shadow-none placeholder:text-muted-foreground/60 placeholder:italic focus-visible:border-0 focus-visible:ring-0 sm:text-3xl"
          />
        </div>

        <p className="px-6 pb-3 text-xs text-muted-foreground">
          Only title and description are needed to create a draft.
        </p>
        <div className="px-6 pb-3">
          <BiasCoachBanner findings={biasFindings} source={draftId ? 'edit' : 'generate'} />
        </div>

        <Label htmlFor="artifact-create-job-description" className="sr-only">
          Job description (required)
        </Label>
        <RichTextEditor
          id="artifact-create-job-description"
          frame="canvas"
          value={descriptionDraft.value}
          onChange={(description) => {
            descriptionDraft.onChange(description)
            setValidationError(null)
            setMutationError(null)
          }}
          maxLength={MARKDOWN_FIELD_MAX_LENGTH}
          onCharCountChange={descriptionDraft.onCharCountChange}
          aria-invalid={
            descriptionDraft.charCount > MARKDOWN_FIELD_MAX_LENGTH ||
            (!descriptionDraft.value.trim() && validationError != null)
          }
          className="min-h-0 flex-1"
        />

        <ArtifactCanvasInlineError>{errorMessage}</ArtifactCanvasInlineError>
      </form>

      <ArtifactCanvasFooterBar
        footerClassName="shrink-0"
        leading={
          <RichTextCharCount
            current={descriptionDraft.charCount}
            max={MARKDOWN_FIELD_MAX_LENGTH}
          />
        }
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={persistJob}
            isDirty
            isSaving={isSaving}
            saveLabel="Create job"
            savingLabel="Creating…"
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
